import React, {
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  useMemo,
} from "react";

import groupIssuesForChecklist from "common/utils/issues/group_issues_for_checklist";
import eliminateSimilarIssues from "common/utils/issues/eliminate_similar_issues";
import addSubissueDataToGroupedIssues from "common/utils/issues/add_subissue_data_to_grouped_issues";
import calculateIssueOverrides from "utils/issues/calculate_issue_overrides";
import {getFilteredIssuesets} from "../utils/issueset";
import Permissioner from "utils/permissioner";
import {Tabs, Tab, AppBar, Toolbar, Typography} from "@material-ui/core";
import PlaybooksToolbar from "./toolbar";
import IssuesPanel from "./issues_panel";
import ContractTypesList, {ContractType} from "./contract_types_list";
import IssueOverridesEditor from "./issue_overrides_editor";
import PromptEditor from "./prompt_editor";

import {
  Issue,
  IssueId,
  PerIssuesetOverrideTagValues,
  PlaybookIssue,
} from "common/types/issue";
import {User} from "common/types/user";
import {IssuesetId, PlaybookIssueset} from "common/types/issueset";
import {OrganisationId} from "common/types/organisation";
import {IssueOverrideTag} from "common/types/issue_override_tag";
import {Project} from "common/types/project";
import {Id} from "common/types";
import {MaybeLoading} from "common/types/util";
import {Workflow, WorkflowId} from "common/flowmaster/types/workflow";

interface PlaybooksComponentProps {
  organisationId: OrganisationId;
  user: User;
  issueOverrideTags: MaybeLoading<IssueOverrideTag[]>;
  fetchIssueOverrideTags: () => void;
  selectedIssueOverrideTag: string;
  onTagChange: (value: string) => void;
  contractTypes: ContractType[];
  projects: Project[];
  issuesById: Record<IssueId, PlaybookIssue>;
  updateIssueset: (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    values: Partial<PlaybookIssueset>,
  ) => void;
  updateIssueOverrideTagValues: (
    issueId: IssueId,
    overrides: PerIssuesetOverrideTagValues,
  ) => void;
  updateIssue: (issue: Issue, data: Partial<Issue>) => void;
  workflows: Workflow[];
  addIssue: (newIssue: Issue) => void;
}

const styles = {
  headerContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  titleMain: {
    fontSize: "1rem",
    marginRight: "1em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: "1rem",
  },
  contentPanel: {
    width: "100%",
    overflowY: "auto",
  },
} as const;

const PlaybooksComponent: React.FC<PlaybooksComponentProps> = ({
  contractTypes,
  organisationId,
  projects,
  issuesById,
  updateIssueset,
  updateIssue,
  fetchIssueOverrideTags,
  issueOverrideTags,
  selectedIssueOverrideTag,
  onTagChange,
  user,
  updateIssueOverrideTagValues,
  workflows,
  addIssue,
}) => {
  const isAdmin = user.is_admin;
  const firstContractType = contractTypes.find(ct =>
    (ct.issuesets || []).find(issueset => !issueset.is_archived),
  );

  const [selectedContractType, setSelectedContractType] = useState<
    ContractType | undefined
  >(firstContractType);
  const [selectedIssueset, setSelectedIssueset] = useState<
    PlaybookIssueset | undefined
  >(
    firstContractType
      ? getFilteredIssuesets(firstContractType, false, isAdmin)[0]
      : undefined,
  );
  const [selectedIssue, setSelectedIssue] = useState<PlaybookIssue | null>(
    null,
  );
  const [issueState, setIssueState] = useState<Record<string, boolean>>({});
  const [showArchivedIssuesets, setShowArchivedIssuesets] = useState(false);
  const [showArchivedIssues, setShowArchivedIssues] = useState(false);
  const [showHiddenIssues, setShowHiddenIssues] = useState(false);

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.ChangeEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const currentSelectedIssueset = useMemo(() => {
    if (!selectedIssueset) {
      return undefined;
    }
    return contractTypes
      .flatMap(contractType => contractType.issuesets)
      .find(issueset => issueset.id === selectedIssueset.id);
  }, [issuesById, contractTypes, selectedIssueset?.id]);

  const canViewIssueTags = useCallback(
    () =>
      Boolean(
        new Permissioner(user).hasPermission("can-view-issue-override-tags"),
      ),
    [user],
  );
  const canViewWorkflowInputOverrides = useCallback(
    () =>
      Boolean(
        new Permissioner(user).hasPermission(
          "can-view-workflow-input-overrides",
        ),
      ),
    [user],
  );
  const canEditWorkflowInputOverrides = useCallback(
    () =>
      Boolean(
        new Permissioner(user).hasPermission(
          "can-edit-workflow-input-overrides",
        ),
      ),
    [user],
  );
  const canSetIssuesetDefaultWorkflow = useCallback(
    () =>
      Boolean(
        new Permissioner(user).hasPermission("can-set-issueset-workflow"),
      ),
    [user],
  );

  useEffect(() => {
    if (canViewIssueTags()) {
      fetchIssueOverrideTags();
    }
  }, [canViewIssueTags, fetchIssueOverrideTags]);

  const handleOverrideTagChange = (event: ChangeEvent<{value: string}>) => {
    onTagChange(event.target.value);
  };

  const toggleIssue = (issueId: IssueId) => {
    setIssueState(prev => ({
      ...prev,
      [issueId]: prev[issueId] === false,
    }));
  };

  const getIssuesets = (contractType: ContractType | undefined) =>
    getFilteredIssuesets(contractType, showArchivedIssuesets, isAdmin);

  const archiveIssueset = (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    isArchived: boolean,
  ) => {
    const newValue = !isArchived;

    if (newValue && selectedContractType) {
      const issuesetsOfSelectedContractType = getFilteredIssuesets(
        selectedContractType,
        false,
        isAdmin,
      ).filter(issueset => issueset.id !== issuesetId);

      if (issuesetsOfSelectedContractType.length > 0) {
        setSelectedIssueset(issuesetsOfSelectedContractType[0]);
      } else {
        const contractTypeWithNonArchivedIssuesets = contractTypes.find(
          ct =>
            ct.id !== contractTypeId &&
            ct.issuesets.find(issueset => !issueset.is_archived),
        );

        if (contractTypeWithNonArchivedIssuesets) {
          const firstNonArchivedIssueset = getFilteredIssuesets(
            contractTypeWithNonArchivedIssuesets,
            false,
            isAdmin,
          )[0];
          if (firstNonArchivedIssueset) {
            setSelectedContractType(contractTypeWithNonArchivedIssuesets);
            setSelectedIssueset(firstNonArchivedIssueset);
          }
        }
      }
    }

    updateIssueset(contractTypeId, issuesetId, {is_archived: newValue});
  };

  const hideIssueset = (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    isHidden: boolean,
  ) => {
    updateIssueset(contractTypeId, issuesetId, {
      is_hidden: !isHidden,
    });
  };

  const starIssueset = (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    isStar: boolean,
  ) => () => {
    updateIssueset(contractTypeId, issuesetId, {
      is_star: !isStar,
    });
  };

  const setIssuesetDefaultWorkflow = (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    defaultWorkflowId: WorkflowId,
  ) => {
    updateIssueset(contractTypeId, issuesetId, {
      default_workflow_id: defaultWorkflowId,
    });
  };

  const selectIssueset = (
    selectedContractType: ContractType,
    selectedIssueset: PlaybookIssueset,
  ) => () => {
    setSelectedContractType(selectedContractType);
    setSelectedIssueset(selectedIssueset);

    if (selectedIssue && !selectedIssueset.issues.includes(selectedIssue.id)) {
      setSelectedIssue(null);
    }
  };

  const selectIssue = (selectedIssue: PlaybookIssue) => () => {
    setSelectedIssue(selectedIssue);
  };

  const triggerShowArchivedIssuesets = () => {
    const newValue = !showArchivedIssuesets;
    setShowArchivedIssuesets(newValue);

    if (!newValue && selectedIssueset?.is_archived && selectedContractType) {
      const firstNonArchivedIssueset = getIssuesets(selectedContractType).find(
        issueset => !issueset.is_archived,
      );

      if (firstNonArchivedIssueset) {
        setSelectedIssueset(firstNonArchivedIssueset);
      } else {
        const contractTypeWithNonArchivedIssuesets = contractTypes.find(ct =>
          ct.issuesets.find(issueset => !issueset.is_archived),
        );
        if (contractTypeWithNonArchivedIssuesets) {
          const firstNonArchivedIssueset = getIssuesets(
            contractTypeWithNonArchivedIssuesets,
          ).find(issueset => !issueset.is_archived);

          setSelectedContractType(contractTypeWithNonArchivedIssuesets);
          if (firstNonArchivedIssueset) {
            setSelectedIssueset(firstNonArchivedIssueset);
          }
        }
      }
    }
  };
  const onAddIssue = issueName => {
    if (!selectedContractType || !selectedIssueset) {
      return;
    }

    const workflowConfig = currentSelectedIssueset?.default_workflow_id
      ? {[currentSelectedIssueset.default_workflow_id]: ""}
      : {};

    const newIssue = {
      name: issueName,
      display_name: issueName,
      contract_types: [
        {
          ...selectedContractType,
          contract_type_id: selectedContractType?.id,
          issuesets: [selectedIssueset],
        },
      ],
      open_subissues_when: "issue_is_triggered",
      show_if_triggered_only: "always",
      hide_subissue_when_parent: "never",
      empty_parent_applicable_clauses: "all_clauses",
      reason_template: "",
      detailed_reason: "",
      positive_reason: "",
      detailed_positive_reason: "",
      about_description: "",
      email_template: "",
      standard_language: "",
      guidance: "",
      positive_guidance: "",
      llm_settings: {
        use_production_mode: true,
        workflow_settings: {
          config: workflowConfig,
          selected_workflow: currentSelectedIssueset?.default_workflow_id,
        },
      },
      issue_class_id: 1,
      issue_order: "",
      trigger_display: true,
      trigger_type: "LLM",
      issue_type: "EMPTY_PARENT",
      rules: {topic_id: null},
      override_values: {},
      styling: {summary: {}},
    };

    addIssue((newIssue as unknown) as Issue);
  };

  const groupedIssues = React.useMemo(() => {
    if (!selectedContractType || !currentSelectedIssueset) {
      return [];
    }

    const {issues: issueIds} = currentSelectedIssueset;
    const issuesWithOverrides = issueIds.reduce<PlaybookIssue[]>(
      (accum, issueId) => {
        const issue = issuesById[issueId];
        if (issue.is_archived && !showArchivedIssues) {
          return accum;
        }
        accum.push(
          calculateIssueOverrides(issue, currentSelectedIssueset, false),
        );
        return accum;
      },
      [],
    );
    const collapsedIssues = eliminateSimilarIssues(issuesWithOverrides);
    const groupedIssuesForChecklist = groupIssuesForChecklist(collapsedIssues);

    if (!showHiddenIssues) {
      return addSubissueDataToGroupedIssues(groupedIssuesForChecklist, false);
    }
    return groupedIssuesForChecklist;
  }, [
    contractTypes,
    selectedContractType,
    currentSelectedIssueset,
    issuesById,
    showArchivedIssues,
    showHiddenIssues,
  ]);

  const overridingIssue = useMemo(() => {
    if (!selectedIssue || !issuesById[selectedIssue.id]) {
      return null;
    }
    const baseIssue = issuesById[selectedIssue.id];
    return {
      ...baseIssue,
      name: selectedIssue.name,
      groupName: selectedIssue.groupName,
    };
  }, [selectedIssue, issuesById, currentSelectedIssueset]);

  return (
    <div className="app-page">
      <PlaybooksToolbar
        showArchivedIssuesets={showArchivedIssuesets}
        showArchivedIssues={showArchivedIssues}
        showHiddenIssues={showHiddenIssues}
        selectedIssueOverrideTag={selectedIssueOverrideTag}
        issueOverrideTags={issueOverrideTags}
        canViewIssueTags={canViewIssueTags()}
        onOverrideTagChange={handleOverrideTagChange}
        onToggleArchivedIssuesets={triggerShowArchivedIssuesets}
        onToggleArchivedIssues={() =>
          setShowArchivedIssues(!showArchivedIssues)
        }
        onToggleHiddenIssues={() => setShowHiddenIssues(!showHiddenIssues)}
        isAdmin={isAdmin}
        tabValue={canViewWorkflowInputOverrides() ? tabValue : 0}
      />
      <div className="app-body" style={{display: "flex", overflowY: "hidden"}}>
        <ContractTypesList
          contractTypes={contractTypes}
          selectedIssueset={currentSelectedIssueset}
          getIssuesets={getIssuesets}
          onSelectIssueset={selectIssueset}
          onStarIssueset={starIssueset}
          isAdmin={isAdmin}
        />
        <IssuesPanel
          selectedContractType={selectedContractType}
          selectedIssueset={currentSelectedIssueset}
          selectedIssue={selectedIssue}
          groupedIssues={groupedIssues}
          issueState={issueState}
          organisationId={organisationId}
          projects={projects}
          onSelectIssue={selectIssue}
          onToggleIssue={toggleIssue}
          onArchiveIssueset={archiveIssueset}
          onHideIssueset={hideIssueset}
          isAdmin={isAdmin}
          workflows={workflows}
          onUpdateDefaultWorkflow={setIssuesetDefaultWorkflow}
          canSetIssuesetDefaultWorkflow={canSetIssuesetDefaultWorkflow()}
          onAddIssue={onAddIssue}
        />
        <div style={styles.contentPanel}>
          {overridingIssue && (
            <>
              {canViewWorkflowInputOverrides() ? (
                <Tabs
                  style={{height: "62px"}}
                  value={tabValue}
                  onChange={handleTabChange}
                >
                  <Tab style={{height: "62px"}} label="Guidance" />
                  <Tab style={{height: "62px"}} label="Rules" />
                </Tabs>
              ) : null}

              <AppBar position="static" style={{backgroundColor: "#232e38"}}>
                <Toolbar>
                  <div style={styles.headerContainer}>
                    <span>
                      {overridingIssue.groupName && (
                        <Typography variant="h6" style={styles.titleMain}>
                          {overridingIssue.groupName}
                        </Typography>
                      )}
                      <Typography variant="h5" style={styles.title}>
                        {overridingIssue.name}
                      </Typography>
                    </span>
                  </div>
                </Toolbar>
              </AppBar>

              {(!canViewWorkflowInputOverrides() || tabValue === 0) && (
                <IssueOverridesEditor
                  issue={overridingIssue}
                  selectedIssueset={currentSelectedIssueset}
                  selectedIssueOverrideTag={selectedIssueOverrideTag}
                  updateIssueOverrideTagValues={updateIssueOverrideTagValues}
                  issueOverrideTags={
                    Array.isArray(issueOverrideTags) ? issueOverrideTags : []
                  }
                />
              )}
              {canViewWorkflowInputOverrides() && tabValue === 1 && (
                <PromptEditor
                  issue={overridingIssue}
                  selectedIssueset={currentSelectedIssueset}
                  organisationId={organisationId}
                  onIssueUpdate={updateIssue}
                  canEditWorkflowInputOverrides={canEditWorkflowInputOverrides()}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaybooksComponent;
